import React, { useState, useEffect } from "react";
import AdminLayout from "./AdminLayout";
import { api } from "../../utils/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem
} from "../../components/ui/select";
import { toast } from "sonner";

export default function UserManagement() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create user form fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [country, setCountry] = useState("");
  const [role, setRole] = useState("operator");

  useEffect(() => {
    fetchAdmins();
    
  }, []);

  const fetchAdmins = async () => {
    try {
      const res = await api.adminListUsers(); // <-- Correct API
      setAdmins(res.data?.users || []);
    } catch (err) {
      toast.error("Failed to load users");
      setAdmins([]);
    } finally {
      setLoading(false);
    }
  };

  

  const handleCreateUser = async () => {
    if (!name || !email || !password || !role) {
      toast.error("All fields are required");
      return;
    }

    try {
      await api.adminCreateUser({
        name,
        email,
        password,
        role,
        country
      });

      toast.success("User created successfully");
      setName("");
      setEmail("");
      setPassword("");
      setRole("operator");
      setCountry("");
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create user");
    }
  };

  const handleChangeRole = async (id, newRole) => {
    try {
      await api.adminUpdateUserRole(id, { role: newRole });
      toast.success("Role updated");
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update role");
    }
  };

  const handleToggleActive = async (id, newStatus) => {
    try {
      await api.adminUpdateUserStatus(id, { is_active: newStatus });
      toast.success("Status updated");
      fetchAdmins();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      await api.adminDeleteUser(id);
      toast.success("User deleted");
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-10 text-center text-gray-500">Loading users...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8">
        <h2 className="text-3xl font-bold">User Management</h2>

        {/* CREATE USER FORM */}
        <div className="bg-white p-6 rounded-lg shadow border space-y-4">
          <h3 className="text-xl font-semibold">Add New User</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              placeholder="Full Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />

            <Input
              placeholder="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <Input
              type="password"
              placeholder="Temporary Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            <Select value={role} onValueChange={setRole}>
              <SelectTrigger>
                <SelectValue placeholder="Select Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="superadmin">Super Admin</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="operator">Operator</SelectItem>
                <SelectItem value="area_manager">Area Manager</SelectItem>

              </SelectContent>
            </Select>

            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger>
                <SelectValue placeholder="Select Country" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="South_Korea">South Korea</SelectItem>
                <SelectItem value="Thailand">Thailand</SelectItem>
                <SelectItem value="India">India</SelectItem>
                
              </SelectContent>
            </Select>

          </div>

          <Button onClick={handleCreateUser}>Create User</Button>
        </div>

        {/* USERS TABLE */}
        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-xl font-semibold mb-4">All Users</h3>

          <table className="w-full border-collapse">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Role</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y">
              {admins.map((item) => (
                <tr key={item.id}>
                  <td className="p-3">{item.name}</td>
                  <td className="p-3">{item.email}</td>

                  {/* ROLE SELECT */}
                  <td className="p-3">
                    <Select
                      value={item.role}
                      onValueChange={(value) => handleChangeRole(item.id, value)}
                    >
                      <SelectTrigger className="w-40 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="superadmin">Super Admin</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="operator">Operator</SelectItem>
                        <SelectItem value="area_manager">Area Manager</SelectItem>

                      </SelectContent>
                    </Select>
                  </td>

                  {/* STATUS */}
                  <td className="p-3">
                    <span
                      className={
                        item.is_active
                          ? "text-green-600 font-medium"
                          : "text-red-600 font-medium"
                      }
                    >
                      {item.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>

                  {/* ACTION BUTTONS */}
                  <td className="p-3 flex space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleToggleActive(item.id, !item.is_active)}
                    >
                      {item.is_active ? "Deactivate" : "Activate"}
                    </Button>

                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteUser(item.id)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {admins.length === 0 && (
            <div className="p-10 text-center text-gray-500">No users found.</div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}